import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Award, AlertTriangle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

// Career path data
interface CareerRolesData {
  currentRole: {
    title: string;
    duration: string;
  };
  desiredRole: {
    title: string;
    timeToTransition: string;
  };
}

const skillsData = {
  currentSkills: {
    Leadership: 3,
    "Strategic Planning": 2,
    "Data Analysis": 4,
    "Stakeholder Communication": 3
  },
  requiredSkills: {
    Leadership: 5,
    "Strategic Planning": 4,
    "Data Analysis": 4,
    "Stakeholder Communication": 5
  }
};

const industryData = {
  industryTrends: ["Data Analytics", "AI Knowledge", "Stakeholder Management"],
  alignmentScore: 75,
  misalignedSkills: ["AI Knowledge"]
};

const resilienceData = {
  resilienceTraits: {
    "Adaptability": 4.2,
    "Resourcefulness": 3.8,
    "Growth Mindset": 3.5
  },
  resilienceInsights: {
    strengths: [
      "You excel in adapting to new challenges and environments, demonstrating flexibility in uncertain situations."
    ],
    areasForImprovement: [
      "Your growth mindset could be strengthened by actively seeking feedback and embracing opportunities for learning."
    ]
  },
  recommendations: [
    "Practice mindfulness to improve stress management during career transitions.",
    "Engage in case-study exercises to enhance problem-solving under pressure.",
    "Take a course on industry trends to prepare for potential pivots."
  ]
};

const brandingData = {
  desiredRole: "Director of Product",
  cvKeywords: [
    "Strategic Leadership",
    "Product Roadmap",
    "Cross-Functional Collaboration",
    "Revenue Growth",
    "Stakeholder Management"
  ],
  linkedinRecommendations: {
    headline: "Add 'Strategic Product Leader' or 'Driving Product Growth and Innovation' to your headline.",
    summary:
      "Incorporate keywords like 'scaling product portfolios,' 'delivering revenue growth,' and 'driving cross-functional collaboration.'",
    skills: ["Product Management", "Strategic Planning", "Data Analytics", "Team Leadership"]
  },
  interviewKeywords: [
    "Customer-Centric",
    "Data-Driven Decision Making",
    "Innovation",
    "Scalable Solutions",
    "Revenue Optimization"
  ]
};

// =============================================================================
// Chart Configurations
// =============================================================================
const skillsChartData = {
  labels: Object.keys(skillsData.currentSkills),
  datasets: [
    {
      label: 'Current Skills',
      data: Object.values(skillsData.currentSkills),
      backgroundColor: 'rgba(238, 153, 234, 0.8)',
      borderColor: 'rgb(238, 153, 234)',
      borderWidth: 1,
      borderRadius: 4
    },
    {
      label: 'Required Skills',
      data: Object.values(skillsData.requiredSkills),
      backgroundColor: 'rgba(88, 211, 226, 0.8)',
      borderColor: 'rgb(88, 211, 226)',
      borderWidth: 1,
      borderRadius: 4
    }
  ]
};

const skillsChartOptions = {
  responsive: true,
  scales: {
    y: {
      beginAtZero: true,
      max: 5,
      grid: { display: false }
    },
    x: { grid: { display: false } }
  },
  plugins: {
    legend: {
      position: 'top',
      labels: { usePointStyle: true, pointStyle: 'circle' }
    }
  }
};

const resilienceChartData = {
  labels: Object.keys(resilienceData.resilienceTraits),
  datasets: [{
    label: 'Resilience Score',
    data: Object.values(resilienceData.resilienceTraits),
    backgroundColor: 'rgba(249, 197, 95, 0.8)',
    borderColor: 'rgb(249, 197, 95)',
    borderWidth: 1,
    borderRadius: 4
  }]
};

const resilienceChartOptions = {
  responsive: true,
  scales: {
    y: {
      beginAtZero: true,
      max: 5,
      grid: { display: false }
    },
    x: { grid: { display: false } }
  },
  plugins: { legend: { display: false } }
};

// =============================================================================
// Reusable ExpandableSection Component
// =============================================================================
const ExpandableSection = ({ isExpanded, toggle, children }) => (
  <>
    <motion.div
      initial={false}
      animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      {children}
    </motion.div>
    <button onClick={toggle} className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1">
      <span>{isExpanded ? 'Read less' : 'Read more'}</span>
      <motion.svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        animate={{ rotate: isExpanded ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </motion.svg>
    </button>
  </>
);

// =============================================================================
// Section Components
// =============================================================================

const SkillGapAnalysisSection = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <section>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Skill Gap Analysis</h2>
      </div>
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Evaluate how your current skills match the requirements of your desired role and uncover areas for growth.
        </p>
        <ExpandableSection isExpanded={expanded} toggle={() => setExpanded(!expanded)}>
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>Discover targeted insights into your strengths and areas for improvement:</p>
            <p><strong className="text-gray-900">Key Insights:</strong> Highlights skills where you excel (e.g., data analysis) and those requiring development (e.g., strategic planning).</p>
            <p><strong className="text-gray-900">Skill Gaps:</strong> Visualized as a bar chart comparing current and required skills.</p>
            <p><strong className="text-gray-900">Development Actions:</strong> Provides specific recommendations like enrolling in leadership programs or taking on cross-functional projects.</p>
          </div>
        </ExpandableSection>
      </div>

      <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
          <div className="h-[400px]">
            <Bar data={skillsChartData} options={skillsChartOptions} />
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-gray-600">Your data analysis skills are well-aligned with role requirements</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-gray-600">Focus on developing strategic planning capabilities for future roles</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-gray-600">Leadership skills need enhancement to meet Director-level expectations</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Development Actions</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="text-secondary mt-1">•</span>
                  <span className="text-gray-600">Enroll in leadership development programs</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-secondary mt-1">•</span>
                  <span className="text-gray-600">Seek strategic planning opportunities in current role</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-secondary mt-1">•</span>
                  <span className="text-gray-600">Build mentorship relationships with senior leaders</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const IndustryAlignmentSection = () => {
  const [expanded, setExpanded] = useState(false);
  const { currentUser } = useAuth();
  const [industryAlignmentData, setIndustryAlignmentData] = useState<{
    industryTrends: Array<{
      name: string;
      score: number;
      status: string;
    }>;
    alignmentScore: number;
    areasForDevelopment: Array<{
      skill: string;
      description: string;
    }>;
  } | null>(null);

  useEffect(() => {
    async function loadIndustryData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.career_accelerator_alingment) {
            const alignmentData = JSON.parse(data.career_accelerator_alingment);
            setIndustryAlignmentData(alignmentData);
          }
        }
      } catch (error) {
        console.error('Error loading industry alignment data:', error);
      }
    }

    loadIndustryData();
  }, [currentUser]);

  return (
    <section>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Industry Alignment</h2>
      </div>
      
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Explore how your skills and aspirations align with industry trends and identify areas to stay competitive.
        </p>
        <ExpandableSection isExpanded={expanded} toggle={() => setExpanded(!expanded)}>
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>Gain insights into how your profile matches evolving industry needs:</p>
            <p><strong className="text-gray-900">Overall Alignment:</strong> A score indicating how well you meet current market demands.</p>
            <p><strong className="text-gray-900">Trends to Watch:</strong> Identifies emerging skills like AI knowledge and stakeholder management to prioritize.</p>
            <p><strong className="text-gray-900">Areas for Development:</strong> Highlights gaps in alignment and suggests targeted upskilling strategies.</p>
          </div>
        </ExpandableSection>
      </div>

      {!industryAlignmentData ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Industry Alignment Data Not Available
          </h3>
          <p className="text-gray-600 max-w-lg mx-auto">
            The industry alignment analysis will be available once your report is generated.
          </p>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Overall Alignment</h3>
                  <div className="text-3xl font-bold text-primary">{industryAlignmentData.alignmentScore}%</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-primary rounded-full h-2.5 transition-all duration-500"
                    style={{ width: `${industryAlignmentData.alignmentScore}%` }}
                  />
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Areas for Development</h3>
                <div className="space-y-4">
                  {industryAlignmentData.areasForDevelopment.map((area, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <span className="text-amber-500 mt-1">•</span>
                      <div>
                        <div className="font-medium text-gray-900">{area.skill}</div>
                        <p className="text-sm text-gray-600">{area.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry Trends</h3>
              <div className="space-y-6">
                {industryAlignmentData.industryTrends.map((trend, index) => (
                  <div key={index} className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-900 font-medium">{trend.name}</span>
                      <span className={`text-sm font-medium ${
                        trend.status === 'development_needed' ? 'text-amber-600' : 'text-green-600'
                      }`}>
                        {trend.status === 'development_needed' ? 'Development Needed' : 'Aligned'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`rounded-full h-2 transition-all duration-500 ${
                          trend.status === 'development_needed' ? 'bg-amber-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${trend.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>)}
    </section>
  );
};

const CareerResilienceSection = () => {
  const [expanded, setExpanded] = useState(false);
  const { currentUser } = useAuth();
  const [resilienceData, setResilienceData] = useState<{
    resilienceTraits: Record<string, number>;
    resilienceInsights: {
      strengths: string[];
      areasForImprovement: string[];
    };
    recommendations: string[];
  } | null>(null);

  useEffect(() => {
    async function loadResilienceData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.career_accelerator_resilience) {
            const resilienceData = JSON.parse(data.career_accelerator_resilience);
            setResilienceData(resilienceData);
          }
        }
      } catch (error) {
        console.error('Error loading resilience data:', error);
      }
    }

    loadResilienceData();
  }, [currentUser]);

  return (
    <section>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Career Resilience Analysis</h2>
      </div>
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Assess your adaptability, resourcefulness, and growth mindset to handle career challenges effectively.
        </p>
        <ExpandableSection isExpanded={expanded} toggle={() => setExpanded(!expanded)}>
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>Unlock insights into your resilience and build strategies for long-term career success:</p>
            <p><strong className="text-gray-900">Resilience Traits:</strong> Visualized scores for adaptability, resourcefulness, and growth mindset.</p>
            <p><strong className="text-gray-900">Strengths:</strong> Key traits that equip you to handle transitions and uncertainty.</p>
            <p><strong className="text-gray-900">Areas for Improvement:</strong> Pinpoints opportunities to grow, like enhancing stress management or developing a proactive learning habit.</p>
            <p><strong className="text-gray-900">Recommendations:</strong> Actionable strategies for building resilience, such as mindfulness or problem-solving workshops.</p>
          </div>
        </ExpandableSection>
      </div>

      {!resilienceData ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Career Resilience Data Not Available
          </h3>
          <p className="text-gray-600 max-w-lg mx-auto">
            The career resilience analysis will be available once your report is generated.
          </p>
        </div>
      ) : (
      <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="h-[300px] mb-6">
                <Bar data={{
                  labels: Object.keys(resilienceData.resilienceTraits),
                  datasets: [{
                    label: 'Resilience Score',
                    data: Object.values(resilienceData.resilienceTraits),
                    backgroundColor: 'rgba(249, 197, 95, 0.8)',
                    borderColor: 'rgb(249, 197, 95)',
                    borderWidth: 1,
                    borderRadius: 4
                  }]
                }} options={resilienceChartOptions} />
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resilience Insights</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Strengths</h4>
                    <ul className="space-y-2">
                      {resilienceData.resilienceInsights.strengths.map((strength, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="text-green-500 mt-1">•</span>
                          <span className="text-gray-600">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Areas for Improvement</h4>
                    <ul className="space-y-2">
                      {resilienceData.resilienceInsights.areasForImprovement.map((area, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="text-amber-500 mt-1">•</span>
                          <span className="text-gray-600">{area}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Building Resilience</h3>
              <div className="space-y-6">
                {resilienceData.recommendations.map((recommendation, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Award className="w-5 h-5 text-primary mt-1" />
                      <div>
                        <p className="text-gray-600">{recommendation}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>)}
    </section>
  );
};

const PersonalBrandingSection = () => {
  const [expanded, setExpanded] = useState(false);
  const { currentUser } = useAuth();
  const [brandingData, setBrandingData] = useState<{
    cvKeywords: string[];
    linkedinRecommendations: {
      headline: string;
      summary: string;
      skills: string[];
    };
    interviewKeywords: string[];
  } | null>(null);

  useEffect(() => {
    async function loadBrandingData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.career_accelerator_branding) {
            const brandingData = JSON.parse(data.career_accelerator_branding);
            setBrandingData(brandingData);
          }
        }
      } catch (error) {
        console.error('Error loading branding data:', error);
      }
    }

    loadBrandingData();
  }, [currentUser]);

  return (
    <section>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Personal Branding Recommendations</h2>
      </div>
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Enhance your personal brand to align with your career goals through CV, LinkedIn, and interview optimization.
        </p>
        <ExpandableSection isExpanded={expanded} toggle={() => setExpanded(!expanded)}>
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>Leverage personalized recommendations to strengthen your professional image:</p>
            <p><strong className="text-gray-900">CV Keywords:</strong> Tailored terms (e.g., "Strategic Leadership") to highlight in your resume.</p>
            <p><strong className="text-gray-900">LinkedIn Optimization:</strong> Improve your headline, summary, and skill endorsements for visibility.</p>
            <p><strong className="text-gray-900">Interview Keywords:</strong> Effective phrases to use in interviews, like "Customer-Centric" and "Data-Driven Decision Making."</p>
            <p><strong className="text-gray-900">Evaluator Feedback Integration:</strong> Incorporate evaluator insights to align your messaging with perceived strengths.</p>
          </div>
        </ExpandableSection>
      </div>

      {!brandingData ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Personal Branding Data Not Available
          </h3>
          <p className="text-gray-600 max-w-lg mx-auto">
            The personal branding recommendations will be available once your report is generated.
          </p>
        </div>
      ) : (
      <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">CV Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {brandingData.cvKeywords.map((keyword, index) => (
                    <span key={index} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Keywords</h3>
                <div className="space-y-3">
                  {brandingData.interviewKeywords.map((keyword, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="text-primary">•</span>
                      <span className="text-gray-600">{keyword}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">LinkedIn Profile Optimization</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Headline Recommendations</h4>
                  <p className="text-gray-600">{brandingData.linkedinRecommendations.headline}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Summary Suggestions</h4>
                  <p className="text-gray-600">{brandingData.linkedinRecommendations.summary}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Key Skills to Highlight</h4>
                  <div className="flex flex-wrap gap-2">
                    {brandingData.linkedinRecommendations.skills.map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>)}
    </section>
  );
};

// =============================================================================
// Main View Component
// =============================================================================
export default function CareerAcceleratorView() {
  const { currentUser } = useAuth();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    careerPath: false,
    skillGap: false,
    industryAlignment: false,
    careerResilience: false,
    personalBranding: false
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const [careerRolesData, setCareerRolesData] = useState<CareerRolesData | null>(null);
  const [readinessData, setReadinessData] = useState<{ readinessScore: number } | null>(null);
  const [challengesData, setChallengesData] = useState<{ challenges: string[] } | null>(null);
  const [skillGapData, setSkillGapData] = useState<{
    currentSkills: Record<string, number>;
    requiredSkills: Record<string, number>;
    keyInsights: string[];
    developmentActions: string[];
  } | null>(null);
  const [brandingData, setBrandingData] = useState<{
    cvKeywords: string[];
    linkedinRecommendations: {
      headline: string;
      summary: string;
      skills: string[];
    };
    interviewKeywords: string[];
  } | null>(null);
  const [resilienceData, setResilienceData] = useState<{
    resilienceTraits: Record<string, number>;
    resilienceInsights: {
      strengths: string[];
      areasForImprovement: string[];
    };
    recommendations: string[];
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadCareerData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          // Load career roles data
          if (data.career_accelerator_roles) {
            const rolesData = JSON.parse(data.career_accelerator_roles);
            setCareerRolesData(rolesData);
          }
          // Load readiness estimate data
          if (data.career_accelerator_readiness_est) {
            const readinessData = JSON.parse(data.career_accelerator_readiness_est);
            setReadinessData(readinessData);
          }
          // Load challenges data
          if (data.career_accelerator_readiness_challenges) {
            const challengesData = JSON.parse(data.career_accelerator_readiness_challenges);
            setChallengesData(challengesData);
          }
          // Load skill gap data
          if (data.career_accelerator_skill_gap) {
            const skillGapData = JSON.parse(data.career_accelerator_skill_gap);
            setSkillGapData(skillGapData);
          }
          // Load branding data
          if (data.career_accelerator_branding) {
            const brandingData = JSON.parse(data.career_accelerator_branding);
            setBrandingData(brandingData);
          }
          // Load resilience data
          if (data.career_accelerator_resilience) {
            const resilienceData = JSON.parse(data.career_accelerator_resilience);
            setResilienceData(resilienceData);
          }
        }
      } catch (error) {
        console.error('Error loading career roles data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadCareerData();
  }, [currentUser]);

  return (
    <div className="p-6 space-y-12">
      <section>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Career Path</h2>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        ) : !careerRolesData ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Career Path Data Not Available
            </h3>
            <p className="text-gray-600 max-w-lg mx-auto">
              The career path information will be available once your report is generated.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
              <p className="text-gray-600 leading-relaxed mb-4">
                Understand where you are in your career journey and how to move forward. This section maps your current role, desired role, and transition challenges.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
                <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Current Role</h3>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                      {careerRolesData.currentRole.duration}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {careerRolesData.currentRole.title}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
                <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Desired Role</h3>
                    <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm">
                      Target: {careerRolesData.desiredRole.timeToTransition}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-gray-900">
                      {careerRolesData.desiredRole.title}
                    </div>
                    {readinessData ? (
                      <div className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        Role Readiness Estimate: {readinessData.readinessScore}% ready
                      </div>
                    ) : (
                      <div className="text-xl font-medium text-gray-400">
                        Readiness estimate unavailable
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          
            {/* Common Transition Challenges */}
            <div className="mt-6">
              <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
                {challengesData ? (
                  <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Transition Challenges</h3>
                    <ul className="space-y-3">
                      {challengesData.challenges.map((challenge, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="text-primary mt-1.5">•</span>
                          <span className="text-gray-600">{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Transition Challenges</h3>
                    <p className="text-gray-500 text-center">Transition challenges data is not available yet.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>
      <section>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Skill Gap Analysis</h2>
        </div>

        <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
          <p className="text-gray-600 leading-relaxed mb-4">
            Evaluate how your current skills match the requirements of your desired role and uncover areas for growth.
          </p>
          
          <motion.div
            initial={false}
            animate={{ height: expandedSections.skillGap ? 'auto' : 0, opacity: expandedSections.skillGap ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
              <p>Discover targeted insights into your strengths and areas for improvement:</p>
              <p><strong className="text-gray-900">Key Insights:</strong> Highlights skills where you excel (e.g., data analysis) and those requiring development (e.g., strategic planning).</p>
              <p><strong className="text-gray-900">Skill Gaps:</strong> Visualized as a bar chart comparing current and required skills.</p>
              <p><strong className="text-gray-900">Development Actions:</strong> Provides specific recommendations like enrolling in leadership programs or taking on cross-functional projects.</p>
            </div>
          </motion.div>
          
          <button
            onClick={() => toggleSection('skillGap')}
            className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
          >
            <span>{expandedSections.skillGap ? 'Read less' : 'Read more'}</span>
            <motion.svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              animate={{ rotate: expandedSections.skillGap ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.svg>
          </button>
        </div>

        {skillGapData ? (
          <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
            <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
              <div className="h-[400px]">
                <Bar data={{
                  labels: Object.keys(skillGapData.currentSkills),
                  datasets: [
                    {
                      label: 'Current Skills',
                      data: Object.values(skillGapData.currentSkills),
                      backgroundColor: 'rgba(238, 153, 234, 0.8)',
                      borderColor: 'rgb(238, 153, 234)',
                      borderWidth: 1,
                      borderRadius: 4
                    },
                    {
                      label: 'Required Skills',
                      data: Object.values(skillGapData.requiredSkills),
                      backgroundColor: 'rgba(88, 211, 226, 0.8)',
                      borderColor: 'rgb(88, 211, 226)',
                      borderWidth: 1,
                      borderRadius: 4
                    }
                  ]
                }} options={skillsChartOptions} />
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
                  <ul className="space-y-3">
                    {skillGapData.keyInsights.map((insight, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-gray-600">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Development Actions</h3>
                  <ul className="space-y-3">
                    {skillGapData.developmentActions.map((action, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="text-secondary mt-1">•</span>
                        <span className="text-gray-600">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Skill Gap Analysis Not Available
            </h3>
            <p className="text-gray-600 max-w-lg mx-auto">
              The skill gap analysis will be available once your report is generated.
            </p>
          </div>
        )}
      </section>
      <IndustryAlignmentSection />
      <CareerResilienceSection />
      <PersonalBrandingSection />
    </div>
  );
}